import { useClaims, useHasPermission } from "../auth/useClaims";

// Technical Work permission codes, all confirmed present in the live
// catalog. These booleans only decide whether to SHOW a control - the server
// re-checks every one of them (and, for the technician-facing actions, an
// ownership check on top), so a wrongly-enabled button just surfaces the
// server's own 400/403 rather than bypassing anything.
export const useCanViewAllTechnical = () => useHasPermission("Technical.View");
export const useCanViewComplaints = () => useHasPermission("Technical.ViewComplaints");
export const useCanViewServiceVisits = () => useHasPermission("Technical.ViewServiceVisits");
export const useCanViewInstallations = () => useHasPermission("Technical.ViewInstallations");

export const useCanManageWork = () => useHasPermission("Technical.ManageWork");
export const useCanCloseWork = () => useHasPermission("Technical.CloseWork");
export const useCanAssignToTechnical = () => useHasPermission("Technical.AssignToTechnical");
export const useCanLogComplaint = () => useHasPermission("Technical.LogComplaint");
export const useCanCreateServiceVisit = () => useHasPermission("Technical.CreateServiceVisit");
export const useCanEditComplaint = () => useHasPermission("Technical.EditComplaint");

// Blanket Technical.View OR any one of the three granular view codes. The
// granular ones don't just decide whether the endpoint 200s - they also
// restrict WHICH rows come back, so a ViewInstallations-only holder gets a
// legitimately shorter list rather than an error.
export const useCanViewTechnical = () => {
  const { permissions } = useClaims();
  return ["Technical.View", "Technical.ViewComplaints", "Technical.ViewServiceVisits", "Technical.ViewInstallations"]
    .some((code) => permissions.includes(code));
};

// The activity timeline endpoint is gated "Technical.ManageWork|Technical.CloseWork"
// - supervisory only, deliberately not visible to a technician login. Checked
// here before the query is even fired, so a technician-shaped account never
// sees a failed request where a card should be.
export const useCanViewActivity = () => {
  const { permissions } = useClaims();
  return permissions.includes("Technical.ManageWork") || permissions.includes("Technical.CloseWork");
};

// The technician picker reads GET /masters/employees?departmentNameLike=TECHNICAL
// and the Log Complaint customer search reads
// GET /masters/customers/search-all-branches.
//
// Neither requires the master's own ".View" any more. Standing project rule
// (2026-09-08, direct instruction): a master's .View must NOT be an extra
// hurdle for someone who already holds the create/edit/assign permission that
// inherently needs to pick from that master - "its very complicated to
// remember the permission sets for these minor things". Both endpoints now
// accept the Technical codes directly, so holding Technical.ManageWork is
// enough to pick a technician and Technical.LogComplaint /
// .CreateServiceVisit is enough to pick a customer.
//
// These mirror the server's own widened policies, so the UI and the API agree
// on who may open a picker - they are NOT a second, stricter gate.
export const useCanSearchEmployees = () => {
  const { permissions } = useClaims();
  return ["Employees.View", "Technical.ManageWork", "Technical.LogComplaint", "Technical.CreateServiceVisit"]
    .some((code) => permissions.includes(code));
};
export const useCanSearchCustomers = () => {
  const { permissions } = useClaims();
  return ["Customers.View", "Reports.StockMovement", "Technical.LogComplaint", "Technical.CreateServiceVisit"]
    .some((code) => permissions.includes(code));
};
